/*global google*/
import React, { FunctionComponent, useCallback, useContext } from "react";
import { Marker, InfoWindow, useGoogleMap, MarkerProps } from "@react-google-maps/api";
import { theme } from "../../theme/theme";
import StartIcon from "../../assets/images/google-maps/route-start.png";
import EndIcon from "../../assets/images/google-maps/route-end.png";
import { MarkerType } from "./custom-map";
import { RouteContext } from "../../pages/route-details/partials/route-context";

interface ICustomMarkerProps extends MarkerProps {
  id: string;
  onClick?: () => void;
  number?: string;
  type?: Exclude<MarkerType, "circle">;
  disableInfoWindow?: boolean;
  renderCustomInfoWindow?: (markerId: string) => JSX.Element | "";
}

export const CustomMarker: FunctionComponent<ICustomMarkerProps> = (props) => {
  const {
    id,
    position,
    number,
    type,
    onClick,
    renderCustomInfoWindow = () => {
      return "";
    },
    draggable,
    ...other
  } = props;
  const instance = useGoogleMap();
  const { selectedStopId, setSelectedStopId } = useContext(RouteContext);

  const onMarkerClicked = useCallback(() => {
    if (instance) {
      setSelectedStopId(id);
    }

    if (onClick) {
      onClick();
    }
  }, [instance, onClick, id, setSelectedStopId]);

  const getIcon = useCallback((): google.maps.Icon | string => {
    let color = theme.PRIMARY_COLOR.main.replace("#", "");
    const text = number || "%E2%80%A2";
    if (!type) {
      return `https://chart.apis.google.com/chart?chst=d_map_spin&chld=0.6|0|${color}|10|b|${text}`;
    }

    const iconSize = new google.maps.Size(36, 36);
    switch (type) {
      case "start":
        return {
          url: StartIcon,
          scaledSize: iconSize,
        };
      case "end":
        return {
          url: EndIcon,
          scaledSize: iconSize,
        };
      case "default":
      default:
        color = "e86161";
    }

    return `https://chart.apis.google.com/chart?chst=d_map_spin&chld=0.5|0|${color}|10|b|${text}`;
  }, [number, type]);

  const infoWindowPixelOffset = new google.maps.Size(0, -30.75);
  return (
    <Marker
      key={`map_${id}`}
      icon={getIcon()}
      onClick={onMarkerClicked}
      position={position}
      draggable={selectedStopId === id && draggable}
      {...other}
    >
      {selectedStopId === id && (
        <InfoWindow
          onCloseClick={() => setSelectedStopId(null)}
          options={{
            pixelOffset: infoWindowPixelOffset,
          }}
          position={position}
        >
          {renderCustomInfoWindow(id)}
        </InfoWindow>
      )}
    </Marker>
  );
};
